import React from "react";
// Removed framer-motion import
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExternalLinkAlt } from '@fortawesome/free-solid-svg-icons';

// Define different color themes
// Retained for potential future use or if dynamic theming without animation is intended
const themes = {
  light: {
    '--primary-bg': '#f8f9fa', // Light background
    '--text-title': '#007bff', // Professional blue
    '--text-subtitle': '#6c757d',
    '--text-card-name': '#343a40',
    '--text-card-tagline': '#495057',
    '--text-card-description': '#6c757d',
    '--icon-color': '#adb5bd',
    '--icon-hover-color': '#0056b3',
    '--card-bg': '#ffffff',
    '--card-border': '#e9ecef',
    '--card-shadow': 'rgba(0, 0, 0, 0.1)',
  },
  dark: {
    '--primary-bg': '#000000', // Black background
    '--text-title': '#007bff', // Professional blue for dark theme
    '--text-subtitle': '#adb5bd',
    '--text-card-name': '#f8f9fa',
    '--text-card-tagline': '#ced4da',
    '--text-card-description': '#adb5bd',
    '--icon-color': '#ced4da',
    '--icon-hover-color': '#0056b3',
    '--card-bg': '#1a1a1a', // Dark card background
    '--card-border': '#343a40',
    '--card-shadow': 'rgba(0, 0, 0, 0.4)',
  },
  // Added a professional corporate blue theme
  corporateBlue: {
    '--primary-bg': '#0a192f', // Deep corporate blue
    '--text-title': '#64ffda', // Light green-blue accent
    '--text-subtitle': '#8892b0',
    '--text-card-name': '#e6f1ff',
    '--text-card-tagline': '#ccd6f6',
    '--text-card-description': '#8892b0',
    '--icon-color': '#ccd6f6',
    '--icon-hover-color': '#64ffda',
    '--card-bg': '#112240', // Slightly lighter corporate blue for cards
    '--card-border': '#233554',
    '--card-shadow': 'rgba(0, 0, 0, 0.5)',
  },
};

const projects = [
  {
    name: "Shadivivastha.com",
    tagline: "Your dream wedding, perfectly planned.",
    description: "A comprehensive wedding management platform connecting vendors, venues, and couples for seamless wedding planning.",
    image: "sv.jpeg",
    link: "https://shadivivastha.com/",
  },
  {
    name: "GirrajMedicare.com",
    tagline: "Wellness at your fingertips.",
    description: "Connecting you with healthcare solutions, offering a platform for purchasing medicines and accessing doctor consultations.",
    image: "gm.jpeg",
    link: "https://peaceful-manatee-07e3ef.netlify.app/",
  },
  {
    name: "Leleproperty.com",
    tagline: "Finding your space, simplified.",
    description: "A real estate platform for buying, selling, and renting properties with advanced search and filtering options.",
    image: "lp.jpeg",
    link: "https://leleproperty.com/",
  },
  {
    name: "Girrajtechnology.com",
    tagline: "Innovate. Transform. Thrive.",
    description: "Dynamic software company providing custom solutions, digital transformation, and tech-driven services.",
    image: "GT.jpeg",
    link: "https://girrajtechnology.com/",
  },
  {
    name: "JoinVyapar.com",
    tagline: "Grow your business, together.",
    description: "Explore opportunities as a Vyapar Distributor, Seva Kendra Franchise, or investor for income generation and business growth.",
    image: "abu.png",
    link: "https://joinvyapar.com/",
  },
];

// Default to 'dark' theme for consistency with previous requests
const Project = ({ themeName = "dark" }) => {
  const currentTheme = themes[themeName] || themes.dark; // Fallback to dark if themeName is invalid

  return (
    <div
      className="project-page-container"
      style={{
        '--primary-bg': currentTheme['--primary-bg'],
        '--text-title': currentTheme['--text-title'],
        '--text-subtitle': currentTheme['--text-subtitle'],
        '--text-card-name': currentTheme['--text-card-name'],
        '--text-card-tagline': currentTheme['--text-card-tagline'],
        '--text-card-description': currentTheme['--text-card-description'],
        '--icon-color': currentTheme['--icon-color'],
        '--icon-hover-color': currentTheme['--icon-hover-color'],
        '--card-bg': currentTheme['--card-bg'],
        '--card-border': currentTheme['--card-border'],
        '--card-shadow': currentTheme['--card-shadow'],
      }}
    >
      <h1 className="projects-title">
        Our Innovative Projects
      </h1>
      <p className="projects-subtitle">
        Discover the solutions we've crafted to empower businesses and individuals alike.
      </p>

      <div className="projects-grid">
        {projects.map((project, index) => (
          <a
            key={project.name}
            href={project.link}
            target="_blank"
            rel="noopener noreferrer"
            className="project-card-link"
          >
            <div
              className="project-card"
            >
              <div className="project-image-container">
                <img
                  src={project.image}
                  alt={project.name}
                  className="project-image"
                />
                <div className="project-image-overlay"></div>
              </div>
              <h3 className="project-name">
                {project.name}
                <FontAwesomeIcon icon={faExternalLinkAlt} className="external-link-icon" />
              </h3>
              <p className="project-tagline">{project.tagline}</p>
              <p className="project-description">{project.description}</p>
            </div>
          </a>
        ))}
      </div>

      {/* Styles */}
      <style jsx global>{`
        @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700&display=swap');
        @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;500;600&display=swap');

        /* CSS Variables will be set by the component's style prop */
        :root {
          /* Default values for fallback if not set by component props */
          --primary-bg: #000000;
          --text-title: #007bff;
          --text-subtitle: #adb5bd;
          --text-card-name: #f8f9fa;
          --text-card-tagline: #ced4da;
          --text-card-description: #adb5bd;
          --icon-color: #ced4da;
          --icon-hover-color: #0056b3;
          --card-bg: #1a1a1a;
          --card-border: #343a40;
          --card-shadow: rgba(0, 0, 0, 0.4);
        }

        body {
          background-color: #000000; /* Ensures the whole page background is black */
        }

        .project-page-container {
          background-color: var(--primary-bg); /* Uses dynamic theme background */
          position: relative;
          overflow: hidden;
          padding: 60px 20px;
          min-height: 100vh;
          display: flex;
          justify-content: flex-start;
          align-items: center;
          flex-direction: column;
          font-family: 'Poppins', sans-serif;
          color: var(--text-card-description); /* Default text color for the container */
        }

        .projects-title {
          text-align: center;
          margin-bottom: 15px;
          font-size: 4em;
          font-family: 'Poppins', sans-serif; /* Changed to Poppins for consistency */
          font-weight: 700;
          position: relative;
          z-index: 1;
          letter-spacing: 1px;
          color: var(--text-title); /* Dynamic color */
          text-shadow: none; /* Removed text shadow for cleaner look */
        }

        /* Removed .gradient-text as it implies animation/complex styling */

        .projects-subtitle {
          text-align: center;
          margin-bottom: 60px;
          font-size: 1.4em;
          color: var(--text-subtitle); /* Dynamic color */
          font-family: 'Poppins', sans-serif; /* Changed to Poppins for consistency */
          font-weight: 400;
          position: relative;
          z-index: 1;
          max-width: 900px;
          line-height: 1.8;
          opacity: 1; /* Ensure full opacity */
        }

        .projects-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
          gap: 40px;
          text-align: center;
          max-width: 1500px;
          width: 100%;
          position: relative;
          z-index: 1;
          padding: 0 30px;
        }

        .project-card-link {
          text-decoration: none;
          color: inherit;
        }

        .project-card {
          border: 1px solid var(--card-border); /* Dynamic border color */
          border-radius: 12px; /* Slightly less rounded for classic look */
          padding: 25px;
          box-shadow: 0 8px 20px var(--card-shadow); /* Simple, professional shadow */
          display: flex;
          flex-direction: column;
          align-items: center;
          cursor: pointer;
          height: 100%;
          background: var(--card-bg); /* Dynamic card background */
          transition: transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out; /* Simple transitions */
          color: var(--text-card-description); /* Default text color for card content */
          position: relative;
          overflow: hidden;
          padding-bottom: 15px;
        }

        /* Removed sparkle ::before element */

        .project-card:hover {
          transform: translateY(-5px); /* Gentle lift on hover */
          box-shadow: 0 12px 25px var(--card-shadow); /* Slightly more pronounced shadow */
        }

        .project-image-container {
          width: 100%;
          height: 220px;
          border-radius: 8px; /* Less rounded image container */
          overflow: hidden;
          position: relative;
          box-shadow: 0 4px 10px rgba(0,0,0,0.2); /* Subtle image shadow */
          margin-bottom: 20px;
        }

        .project-image {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 0.2s ease-in-out; /* Simple image scale transition */
        }

        .project-card:hover .project-image {
          transform: scale(1.02); /* Subtle zoom on hover */
        }

        .project-image-overlay {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: linear-gradient(to top, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0) 60%); /* Subtle overlay */
        }

        .project-name {
          color: var(--text-card-name); /* Dynamic color */
          margin-top: 10px;
          margin-bottom: 5px;
          font-size: 2em; /* Adjusted font size */
          font-family: 'Poppins', sans-serif; /* Changed to Poppins for consistency */
          font-weight: 600; /* Bolder for names */
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          text-shadow: none; /* Removed text shadow */
        }

        .external-link-icon {
          font-size: 0.6em;
          color: var(--icon-color); /* Dynamic color */
          transition: color 0.2s ease, transform 0.2s ease; /* Simple transition */
        }

        .project-card:hover .external-link-icon {
          color: var(--icon-hover-color); /* Dynamic hover color */
          transform: translateX(2px) translateY(-2px); /* Subtle shift */
        }

        .project-tagline {
          color: var(--text-card-tagline); /* Dynamic color */
          font-size: 1.1em;
          font-family: 'Poppins', sans-serif; /* Changed to Poppins for consistency */
          font-weight: 400;
          margin-top: 0;
          margin-bottom: 15px;
          text-align: center;
          opacity: 0.9;
        }

        .project-description {
          color: var(--text-card-description); /* Dynamic color */
          font-size: 0.95em; /* Adjusted font size */
          font-family: 'Poppins', sans-serif; /* Changed to Poppins for consistency */
          line-height: 1.6;
          letter-spacing: 0.1px;
          flex-grow: 1;
          text-align: center;
          opacity: 0.8;
          max-height: 80px;
          overflow: hidden;
          text-overflow: ellipsis;
          display: -webkit-box;
          -webkit-line-clamp: 3;
          -webkit-box-orient: vertical;
        }

        /* Responsive Styles */
        @media (max-width: 1200px) {
          .projects-title {
            font-size: 3.5em;
          }
          .projects-grid {
            grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
            gap: 30px;
          }
          .project-card {
              padding: 20px;
              border-radius: 10px;
          }
          .project-image-container {
              height: 200px;
          }
        }

        @media (max-width: 768px) {
          .project-page-container {
              padding: 40px 15px;
          }
          .projects-title {
            font-size: 3em;
            margin-bottom: 10px;
          }
          .projects-subtitle {
              font-size: 1.2em;
              margin-bottom: 40px;
              padding: 0 10px;
          }
          .projects-grid {
            gap: 25px;
            grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          }
          .project-card {
              padding: 18px;
              border-radius: 8px;
          }
          .project-image-container {
              height: 180px;
              border-radius: 6px;
          }
          .project-name {
              font-size: 1.8em;
              margin-top: 8px;
          }
          .project-tagline {
              font-size: 1em;
          }
          .project-description {
              font-size: 0.88em;
              -webkit-line-clamp: 4;
          }
        }

        @media (max-width: 480px) {
          .project-page-container {
              padding: 30px 10px;
          }
          .projects-title {
            font-size: 2.5em;
            letter-spacing: 1px;
          }
          .projects-subtitle {
              font-size: 1em;
              margin-bottom: 30px;
          }
          .projects-grid {
            grid-template-columns: 1fr;
            gap: 20px;
          }
          .project-card {
              padding: 15px;
              border-radius: 8px;
          }
          .project-image-container {
              height: 160px;
              border-radius: 4px;
          }
          .project-name {
              font-size: 1.6em;
              margin-top: 5px;
          }
          .project-tagline {
              font-size: 0.9em;
          }
          .project-description {
              font-size: 0.8em;
              -webkit-line-clamp: 5;
          }
        }
      `}</style>
    </div>
  );
};

export default Project;